import React from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';
import {TASK_TYPE_CONFIG} from '../../utils/taskTypeConfig';

import {Box, Chip, Divider, Stack, Typography, screenSizeBigger} from 'views/components';
import {Check} from 'views/icons';
import {HoverToolBox, MarkdownViewer, translate} from 'views/module';

const TaskItem = (props) => {
  const {
    task,
    clientNameByRn = {},
    handleSetStatusToComplete,
    removeTask,
  } = props;
  const isStatusComplete = task.status === 'COMPLETE';
  const largerScreen = screenSizeBigger('sm');

  const isDueDateADate = moment(task.dueDate, moment.ISO_8601, true).isValid();
  const isOverdue = isDueDateADate ?
    moment(task.dueDate).isBefore(moment()) :
    false;
  const infoByRn = new Map((task.connectionInfo ?? [])
      .map((connectionInfo) => [connectionInfo?.connectionRn || connectionInfo?.clientRn, connectionInfo])
      .filter(([connectionRn]) => Boolean(connectionRn)));

  const connectionRns = Array.from(
      new Set([
        ...(task.connections ?? []).map((connection) =>
          typeof connection === 'string' ? connection : (connection?.connectionRn || connection?.clientRn),
        ),
        ...infoByRn.keys(),
      ].filter(Boolean)),
  );

  const connectionDisplayNames = connectionRns
      .map((connectionRn) => {
        const connectionInfo = infoByRn.get(connectionRn);
        const fullName = [connectionInfo?.firstName, connectionInfo?.lastName]
            .filter(Boolean)
            .join(' ')
            .trim();
        return fullName || connectionInfo?.clientName || clientNameByRn[connectionRn] || connectionRn;
      })
      .filter(Boolean);

  const [toolbarVisible, setToolbarVisible] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const rightEdgeAnchorRef = React.useRef(null);

  const handleMouseEnter = () => {
    if (largerScreen) {
      setToolbarVisible(true);
      setAnchorEl(rightEdgeAnchorRef.current);
    }
  };

  const handleMouseLeave = () => {
    if (largerScreen) {
      setToolbarVisible(false);
      setAnchorEl(null);
    }
  };

  React.useEffect(() => {
    setToolbarVisible(false);
    setAnchorEl(null);
  }, [largerScreen]);

  return (
    <Stack
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        opacity: isStatusComplete ? 0.45 : 1,
      }}
    >
      <Stack
        spacing={1.25}
        sx={{
          py: 2,
          pr: 3,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
        >
          <Box
            onClick={() => handleSetStatusToComplete(task.taskRn, isStatusComplete)}
            sx={{
              'width': 24,
              'height': 24,
              'borderRadius': '50%',
              'border': '1.5px solid',
              'borderColor': isStatusComplete ? 'success.main' : 'divider',
              'backgroundColor': isStatusComplete ? 'success.main' : 'transparent',
              'display': 'flex',
              'alignItems': 'center',
              'justifyContent': 'center',
              'cursor': 'pointer',
              '& .todo-check': {
                transition: 'color 120ms ease',
                color: isStatusComplete ? 'common.white' : 'transparent',
                fontSize: 10,
              },
              '&:hover': {
                borderColor: isStatusComplete ? 'success.main' : 'success.main',
              },
              '&:hover .todo-check': {
                color: isStatusComplete ? 'common.white' : 'success.main',
              },
            }}
          >
            <Check className="todo-check" />
          </Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flexWrap: 'wrap',
              flex: 1,
            }}
          >
            {
              task?.type && (() => {
                const type = task.type;
                const typeConfig = TASK_TYPE_CONFIG[type] ?? TASK_TYPE_CONFIG.UNKNOWN;

                return (
                  <Chip
                    key={type}
                    variant="outlined"
                    label={typeConfig.labelKey ? translate(typeConfig.labelKey) : type}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      ...typeConfig.chipSx,
                      borderWidth: 1,
                    }}
                  />
                );
              })()
            }
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                textDecoration: isStatusComplete ? 'line-through' : 'none',
              }}
            >
              {task.title}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          spacing={1}
          sx={{
            pl: '40px',
          }}
        >
          <MarkdownViewer
            value={task.description?.blob || translate('tasks.MyDay.TaskItem.Description.Empty')}
            truncate
          />
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
          >
            {
              isDueDateADate &&
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: isOverdue ? 'error.main' : 'text.secondary',
                }}
              >
                {translate('tasks.MyDay.TaskItem.Due.Label', {
                  time: moment(task.dueDate).format('h:mmA'),
                })}
              </Typography>
            }
            {
              (task.dueDate && !isDueDateADate) &&
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                }}
              >
                {task.dueDate}
              </Typography>
            }
            <Box
              sx={{
                height: 4,
                width: 4,
                borderRadius: '50%',
                backgroundColor: 'text.secondary',
              }}
            />
            {
              connectionDisplayNames.length > 0 &&
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {`[${connectionDisplayNames.join(', ')}]`}
                </Typography>
            }
          </Stack>
        </Stack>
      </Stack>
      <Box
        ref={rightEdgeAnchorRef}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          width: 0,
          height: 0,
        }}
      />
      <HoverToolBox
        toolBar={toolbarVisible}
        anchorEl={anchorEl}
        onClickDelete={() => removeTask(task)}
        hideTool={{
          emoji: true,
          copy: true,
          edit: true,
          delete: false,
        }}
      />
      <Divider />
    </Stack>
  );
};

TaskItem.propTypes = {
  task: PropTypes.shape({
    taskRn: PropTypes.string.isRequired,
    type: PropTypes.string,
    title: PropTypes.string.isRequired,
    clientRn: PropTypes.string,
    clientName: PropTypes.string,
    description: PropTypes.shape({
      blob: PropTypes.string,
      editorType: PropTypes.string,
    }),
    ownerRn: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    connections: PropTypes.arrayOf(PropTypes.string),
    connectionInfo: PropTypes.arrayOf(PropTypes.shape({
      connectionRn: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    })),
    updatedAt: PropTypes.string,
    createdAt: PropTypes.string,
    dueDate: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  clientNameByRn: PropTypes.object,
  handleSetStatusToComplete: PropTypes.func.isRequired,
  removeTask: PropTypes.func,
};

export default (React.memo(TaskItem));

